export type PdfAnalysisStatus = 'allow' | 'block' | 'review' | null;

// Códigos internos usados para resolver el copy del modal de bloqueo (ver PdfInspectorErrorModal).
// 'unknown' es el fallback para cualquier `detail` no reconocido (evita romper la UI si el
// backend agrega/renombra un detalle nuevo).
export type PdfBlockReasonCode = 'corrupted' | 'empty' | 'blocked' | 'password' | 'unknown';

export interface PdfAnalysisResult {
  decision: 'Allow' | 'Block' | 'Review';
  reason: string;
  // Detalle específico devuelto por el análisis de seguridad (ej. "Pdf file contains password").
  // Es el campo que realmente distingue corrupto/vacío/bloqueado/con contraseña; `reason` es
  // siempre un texto genérico y no debe usarse para diferenciar casos en la UI.
  detail?: string;
  isClean?: boolean;
  // Token firmado (HMAC) atado al hash SHA-256 del archivo, presente solo cuando decision='Allow'.
  // Debe reenviarse al backend en el alta (campo pdfInspectionToken/pdfInspectionTokens) para
  // evitar una re-inspección redundante del mismo archivo (ver ECM-13897).
  inspectionToken?: string;
}

export const PDF_ANALYSIS_DECISION_MAP: Record<string, PdfAnalysisStatus> = {
  Allow: 'allow',
  Block: 'block',
  Review: 'review'
};

// Claves de traducción del motivo por defecto según la decisión del análisis
export const PDF_ANALYSIS_DEFAULT_REASONS: Record<Exclude<PdfAnalysisStatus, null>, string> = {
  allow: 'pdfInspector.pdfAnalyzerService.allowReason',
  block: 'pdfInspector.pdfAnalyzerService.blockReason',
  review: 'pdfInspector.pdfAnalyzerService.reviewReason'
};

// Mapeo del `detail` devuelto por el backend hacia un PdfBlockReasonCode interno.
// Se compara en minúsculas y por substring para tolerar variaciones menores del texto del backend.
const PDF_BLOCK_DETAIL_MAP: Array<{ match: string; code: PdfBlockReasonCode }> = [
  { match: 'corrupted', code: 'corrupted' },
  { match: 'invalid', code: 'corrupted' },
  { match: 'empty', code: 'empty' },
  { match: 'password', code: 'password' },
  { match: 'blocked by security', code: 'blocked' }
];

export function resolvePdfBlockReasonCode(detail?: string): PdfBlockReasonCode {
  if (!detail) {
    return 'unknown';
  }
  const normalized = detail.toLowerCase();
  const found = PDF_BLOCK_DETAIL_MAP.find(entry => normalized.includes(entry.match));
  return found?.code ?? 'unknown';
}
